import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Category = {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  parent_id: string | null;
};

export type KnowledgeAreaWithCount = Category & {
  article_count: number;
};

export type Author = {
  id: string;
  user_id: string;
  name: string;
  bio: string | null;
  avatar_url: string | null;
  created_at: string;
};

export type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image: string | null;
  author_id: string;
  category_id: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  likes_count: number;
  user_has_liked?: boolean;
  tags?: string[];
  // Join fields
  category?: Category;
  author?: Author;
};

export type Tag = {
  name: string;
  count: number;
};

export type SubscriptionStatus = 'success' | 'error' | 'loading' | null;

// Local storage key for liked articles
const LIKED_ARTICLES_KEY = 'rodadeproa_liked_articles';

// Helper function to get liked articles from localStorage
function getLikedArticles(): string[] {
  const liked = localStorage.getItem(LIKED_ARTICLES_KEY);
  return liked ? JSON.parse(liked) : [];
}

// Helper function to save liked articles to localStorage
function saveLikedArticles(articles: string[]) {
  localStorage.setItem(LIKED_ARTICLES_KEY, JSON.stringify(articles));
}

// Utility function to get the full image URL from Supabase
export function getSupabaseImageUrl(imagePath: string | null): string | null {
  if (!imagePath) {
    console.debug('Image path is null');
    return null;
  }

  try {
    console.debug('Processing image path:', imagePath);
    // Tenta decodificar a URL se estiver codificada
    const decodedPath = decodeURIComponent(imagePath);
    console.debug('Decoded path:', decodedPath);

    // Se for um JSON string, extrai a URL
    if (typeof decodedPath === 'string' && (decodedPath.includes('imagem_url') || decodedPath.includes('{'))) {
      try {
        const parsed = JSON.parse(decodedPath);
        console.debug('Parsed JSON:', parsed);
        if (parsed && parsed.imagem_url) {
          console.debug('Returning JSON image URL:', parsed.imagem_url);
          return parsed.imagem_url;
        }
      } catch (e) {
        console.error('Error parsing JSON image:', e);
      }
    }

    // Se a URL já estiver completa, retorna ela
    if (decodedPath.startsWith('http')) {
      console.debug('Returning complete URL:', decodedPath);
      return decodedPath;
    }

    // Se for um caminho simples, constrói a URL completa
    const cleanPath = decodedPath.startsWith('/') ? decodedPath.slice(1) : decodedPath;
    const fullUrl = `${supabaseUrl}/storage/v1/object/public/imagem/${cleanPath}`;
    console.debug('Returning constructed URL:', fullUrl);
    return fullUrl;
  } catch (e) {
    console.error('Error processing image URL:', e, 'Original path:', imagePath);
    return null;
  }
}

export async function getCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('name');
  
  if (error) throw error;
  return data as Category[];
}

export async function getCategoryBySlug(slug: string): Promise<Category | null> {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      console.error('Error fetching category by slug:', error);
      return null;
    }

    return data as Category;
  } catch (e) {
    console.error('Error in getCategoryBySlug:', e);
    return null;
  }
}

export async function getKnowledgeAreasWithArticles(): Promise<KnowledgeAreaWithCount[]> {
  try {
    const { data: knowledgeCategory, error: knowledgeError } = await supabase
      .from('categories')
      .select('id')
      .eq('slug', 'conhecimento')
      .single();

    if (knowledgeError || !knowledgeCategory) {
      console.error('Error fetching Conhecimento category:', knowledgeError);
      return [];
    }

    const { data: childCategories, error: categoriesError } = await supabase
      .from('categories')
      .select('*')
      .eq('parent_id', knowledgeCategory.id)
      .order('name');

    if (categoriesError) {
      console.error('Error fetching knowledge areas:', categoriesError);
      return [];
    }

    if (!childCategories || childCategories.length === 0) {
      return [];
    }

    const { data: publishedArticles, error: articlesError } = await supabase
      .from('articles')
      .select('category_id')
      .eq('published', true)
      .in(
        'category_id',
        childCategories.map(category => category.id)
      );

    if (articlesError) {
      console.error('Error checking published knowledge articles:', articlesError);
      return [];
    }

    const articleCountByCategory = new Map<string, number>();

    for (const article of publishedArticles ?? []) {
      const currentCount = articleCountByCategory.get(article.category_id) ?? 0;
      articleCountByCategory.set(article.category_id, currentCount + 1);
    }

    return (childCategories as Category[])
      .map(category => ({
        ...category,
        article_count: articleCountByCategory.get(category.id) ?? 0
      }))
      .filter(category => category.article_count > 0);
  } catch (e) {
    console.error('Error in getKnowledgeAreasWithArticles:', e);
    return [];
  }
}

export async function getArticles(
  categorySlug?: string,
  searchQuery?: string,
  tags?: string[]
): Promise<Article[]> {
  try {
    let query = supabase
      .from('articles')
      .select(`
        *,
        category:categories!inner(*),
        author:authors(*)
      `)
      .eq('published', true);

    if (categorySlug) {
      query = query.eq('category.slug', categorySlug);
    }

    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }

    if (tags && tags.length > 0) {
      query = query.contains('tags', tags);
    }

    const { data, error } = await query.order('published_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching articles:', error);
      return [];
    }

    // Add user_has_liked flag based on localStorage and process image URLs
    const likedArticles = getLikedArticles();
    const processedArticles = (data as Article[]).map(article => {
      if (article.featured_image) {
        try {
          const processedUrl = getSupabaseImageUrl(article.featured_image);
          if (processedUrl) {
            article.featured_image = processedUrl;
          }
        } catch (e) {
          console.error('Error processing image URL:', e);
        }
      }

      return {
        ...article,
        user_has_liked: likedArticles.includes(article.id)
      };
    });

    return processedArticles;
  } catch (e) {
    console.error('Error in getArticles:', e);
    return [];
  }
}

export async function getArticleBySlug(slug: string): Promise<Article | null> {
  try {
    const { data: articles, error } = await supabase
      .from('articles')
      .select(`
        *,
        category:categories!inner(*),
        author:authors(*)
      `)
      .eq('slug', slug)
      .eq('published', true)
      .single();

    if (error) {
      console.error('Error fetching article:', error);
      return null;
    }

    if (!articles) {
      console.log('No article found with slug:', slug);
      return null;
    }

    // Processa a URL da imagem antes de retornar
    if (articles.featured_image) {
      try {
        const processedUrl = getSupabaseImageUrl(articles.featured_image);
        if (processedUrl) {
          articles.featured_image = processedUrl;
        }
      } catch (e) {
        console.error('Error processing image URL:', e);
      }
    }

    // Adiciona flag de curtida baseado no localStorage
    const likedArticles = getLikedArticles();
    return {
      ...articles,
      user_has_liked: likedArticles.includes(articles.id)
    };
  } catch (e) {
    console.error('Error in getArticleBySlug:', e);
    return null;
  }
}

export async function getPopularTags(): Promise<Tag[]> {
  try {
    // Temporariamente retornando array vazio até resolver o problema da coluna tags
    console.log('Tags feature temporarily disabled');
    return [];

    /* Código original comentado
    const { data: articles, error } = await supabase
      .from('articles')
      .select('tags')
      .eq('published', true)
      .not('tags', 'is', null);

    if (error) {
      console.error('Error fetching tags:', error);
      return [];
    }

    // Conta a frequência de cada tag
    const tagCounts = articles.reduce((acc: Record<string, number>, article) => {
      if (article.tags && Array.isArray(article.tags)) {
        article.tags.forEach((tag: string) => {
          acc[tag] = (acc[tag] || 0) + 1;
        });
      }
      return acc;
    }, {});

    // Converte para o formato Tag[] e ordena por contagem
    return Object.entries(tagCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    */
  } catch (error) {
    console.error('Error processing tags:', error);
    return [];
  }
}

export async function subscribeToNewsletter(email: string) {
  const { error } = await supabase
    .from('subscribers')
    .insert([{ email }]);
  
  if (error) {
    if (error.code === '23505') { // Unique violation
      throw new Error('Este email já está inscrito em nossa newsletter.');
    }
    throw error;
  }
  
  return true;
}

export async function toggleArticleLike(articleId: string): Promise<boolean> {
  const likedArticles = getLikedArticles();
  const isLiked = likedArticles.includes(articleId);

  if (isLiked) {
    // Unlike the article
    const newLikedArticles = likedArticles.filter(id => id !== articleId);
    saveLikedArticles(newLikedArticles);
    
    // Update likes count in database
    await supabase.rpc('decrement_article_likes', { article_id: articleId });
    
    return false;
  } else {
    // Like the article
    likedArticles.push(articleId);
    saveLikedArticles(likedArticles);
    
    // Update likes count in database
    await supabase.rpc('increment_article_likes', { article_id: articleId });
    
    return true;
  }
}